import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { tiempoRelativo } from '../utils/helpers';

/**
 * Card del resumen ejecutivo semanal (output IA).
 * Estados:
 *   - cargando inicial (resumen=null, generating=true) -> skeleton lineas
 *   - sin cache (resumen=null, generating=false) -> empty con CTA "Generar"
 *   - con resumen -> parrafo + timestamp + boton regenerar
 */
const ResumenEjecutivoCard = ({
  resumen,        // { texto, generado_en, vigente_hasta } o null
  generating,     // bool
  error = null,
  onRegenerar,
}) => {
  const tieneTexto = resumen && resumen.texto;

  return (
    <div className="relative bg-white rounded-xl border border-slate-200/70 p-5 overflow-hidden">
      {/* Decoracion sutil */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/60 to-transparent" />

      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-cyan-50 flex items-center justify-center flex-shrink-0">
            <Sparkles size={14} className="text-cyan-600" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
              Esta semana
            </h3>
            {tieneTexto && resumen.generado_en && (
              <p className="text-[10px] text-slate-400 tabular-nums">
                Generado {tiempoRelativo(resumen.generado_en)}
              </p>
            )}
          </div>
        </div>

        {(tieneTexto || error) && (
          <button
            onClick={onRegenerar}
            disabled={generating}
            title="Regenerar resumen"
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating
              ? <Loader2 size={11} className="animate-spin" />
              : <RefreshCw size={11} strokeWidth={2} />
            }
            Regenerar
          </button>
        )}
      </div>

      {generating && !tieneTexto && (
        <div className="space-y-2 skeleton">
          {[100, 90, 95, 65].map((w, i) => (
            <div key={i} className="h-3 bg-slate-100 rounded" style={{ width: `${w}%` }} />
          ))}
        </div>
      )}

      {tieneTexto && (
        <motion.p
          key={resumen.generado_en}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="text-sm leading-relaxed text-slate-700 whitespace-pre-line"
        >
          {resumen.texto}
        </motion.p>
      )}

      {!generating && !tieneTexto && !error && (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            Generamos un resumen IA con lo que pasó esta semana.
          </p>
          <button
            onClick={onRegenerar}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98] flex-shrink-0"
          >
            <Sparkles size={13} strokeWidth={2.2} />
            Generar
          </button>
        </div>
      )}

      {!generating && error && (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-rose-600">
            No pudimos generar el resumen.
          </p>
          <button
            onClick={onRegenerar}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
          >
            <RefreshCw size={11} strokeWidth={2} />
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
};

export default ResumenEjecutivoCard;
